import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { dialogs, i18next } from '@yola/ws-sdk';
import ColorSettingsDialog from '../components/color-settings-dialog';
import parseHslColorString from '../helpers/parse-hsl-color-string';
import parseHslaColorString from '../helpers/parse-hsla-color-string';
import supportedColorModes from '../constants/supported-color-modes';
import constants from '../constants';

const { DEFAULT_OFFSET_X, DEFAULT_OFFSET_Y } = constants;

const getCaptions = () => ({
  title: i18next.t('Color settings'),
  apply: i18next.t('Save'),
  cancel: i18next.t('Cancel'),
  opacity: i18next.t('Opacity'),
});

function ColorPickerContainer({
  color,
  getSharedData,
  onDialogMainAction,
  onDialogCancel,
  onColorChange = null,
  enableAlpha = false,
  callerDialogType = null,
  callerDialogProps = null,
}) {
  const dispatch = useDispatch();
  const sharedData = getSharedData();
  const initialOffsetX = sharedData.offsetX ?? DEFAULT_OFFSET_X;
  const initialOffsetY = sharedData.offsetY ?? DEFAULT_OFFSET_Y;

  const [hslaColor, setHslaColor] = useState(parseHslaColorString(color));
  const [dialogPosition, setDialogPosition] = useState({
    offsetX: initialOffsetX,
    offsetY: initialOffsetY,
  });

  const handleColorChange = ({ hsl }) => {
    setHslaColor({ ...hsl });
    if (onColorChange) {
      onColorChange({ hsl });
    }
  };

  const navigateBack = (extraProps = {}) => {
    if (callerDialogType) {
      dispatch(dialogs.actions.show(callerDialogType, { ...callerDialogProps, ...extraProps }));
    } else {
      dialogs.operations.hide();
    }
  };

  const handleSubmit = () => {
    onDialogMainAction({
      color: hslaColor,
      offsetX: dialogPosition.offsetX,
      offsetY: dialogPosition.offsetY,
    });
    navigateBack();
  };

  const handleCancel = () => {
    onDialogCancel({
      offsetX: dialogPosition.offsetX,
      offsetY: dialogPosition.offsetY,
    });
    navigateBack();
  };

  const handleDragEnd = (_, { x, y }) => {
    setDialogPosition({ offsetX: x, offsetY: y });
  };

  const handlePredefinedColorClick = ({ hslString, opacity: alpha }) => {
    const { h, s, l } = parseHslColorString(hslString);
    handleColorChange({ hsl: { h, s, l, a: alpha } });
  };

  const captions = getCaptions();

  return (
    <ColorSettingsDialog
      captions={captions}
      color={hslaColor}
      enableAlpha={enableAlpha}
      offsetX={dialogPosition.offsetX}
      offsetY={dialogPosition.offsetY}
      supportedModes={[supportedColorModes.SOLID]}
      onColorChange={handleColorChange}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onDragEnd={handleDragEnd}
      onPredefinedColorClick={handlePredefinedColorClick}
      mode={supportedColorModes.SOLID}
    />
  );
}

ColorPickerContainer.propTypes = {
  color: PropTypes.string.isRequired,
  getSharedData: PropTypes.func.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
  onColorChange: PropTypes.func,
  enableAlpha: PropTypes.bool,
  callerDialogType: PropTypes.string,
  callerDialogProps: PropTypes.shape({}),
};

export default ColorPickerContainer;
